/**
 * CompetitorSpyPage — dedicated tool subpage for live input → result UX.
 * Mirrors BrandMonitorPage but wired to /api/competitor-spy/* endpoints.
 *
 * Flow: user signs in, adds a brand URL + up to 5 competitor URLs, runs a
 * check, and sees a comparison table, prioritised alerts and a top action.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import {
  TrendingUp, GitCompare, Loader2, Plus, X, Trash2, RefreshCw, Zap,
  AlertTriangle, Target, ArrowRight, CheckCircle2,
} from "lucide-react";
import { OrderNowBottomCTA } from "../components/common/ServiceOrderBundle";

const PRIORITY_STYLE = {
  high: "bg-red-900/20 text-red-400 border-red-900/30",
  medium: "bg-amber-900/20 text-amber-400 border-amber-900/30",
  low: "bg-emerald-900/20 text-emerald-400 border-emerald-900/30",
};
const ALERT_ICON = {
  opportunity: <Target className="w-4 h-4" />,
  threat: <AlertTriangle className="w-4 h-4" />,
  change: <RefreshCw className="w-4 h-4" />,
};

export default function CompetitorSpyPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [spies, setSpies] = useState([]);
  const [selected, setSelected] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [form, setForm] = useState({ brand_url: "", competitor_urls: "" });

  useEffect(() => { if (user) fetchSpies(); }, [user]);  // eslint-disable-line

  const fetchSpies = async () => {
    const r = await axios.get(`${API}/competitor-spy/status`, { params: { email: user.email } }).catch(() => null);
    const list = r?.data?.spies || [];
    setSpies(list);
    if (list.length && !selected) { setSelected(list[0]); fetchResults(list[0].spy_id); }
  };
  const fetchResults = async (id) => {
    setLoading(true);
    const r = await axios.get(`${API}/competitor-spy/results/${id}`).catch(() => null);
    setResults(r?.data || null);
    setLoading(false);
  };
  const handleSetup = async (e) => {
    e.preventDefault();
    const comps = form.competitor_urls.split(",").map((c) => c.trim()).filter(Boolean).slice(0, 5);
    if (!form.brand_url || !comps.length) return;
    setLoading(true);
    const r = await axios.post(`${API}/competitor-spy/setup`, {
      brand_url: form.brand_url, competitor_urls: comps, email: user.email, language: language || "en",
    }).catch(() => null);
    if (r?.data?.spy_id) {
      setShowSetup(false);
      setForm({ brand_url: "", competitor_urls: "" });
      await fetchSpies();
      setSelected(r.data);
      await runCheck(r.data.spy_id);
    }
    setLoading(false);
  };
  const runCheck = async (id) => {
    setChecking(true);
    await axios.post(`${API}/competitor-spy/check`, null, { params: { spy_id: id } }).catch(() => null);
    await fetchResults(id);
    setChecking(false);
  };
  const handleDelete = async (id) => {
    await axios.delete(`${API}/competitor-spy/${id}`).catch(() => null);
    setSpies((prev) => prev.filter((s) => s.spy_id !== id));
    if (selected?.spy_id === id) { setSelected(null); setResults(null); }
  };

  const finalCta = (
    <OrderNowBottomCTA
      packageId="addon_competitor_spy"
      priceEur={29}
      interval="mes"
      serviceName="Competitor Spy"
      headline="Buď o 2 kroky pred konkurenciou"
      subcopy="Daily AI citation delta tracking + content change alerts pre až 5 konkurentov. Zisti čo robia skôr než oni."
      testid="competitor-spy-final-cta"
    />
  );

  if (!user) return (
    <SignInGate title={t.cs_title || "Competitor Spy"} sub={t.cs_sign_in_desc || "Sign in to track competitors across SEO + AI visibility."}>
      {finalCta}
    </SignInGate>
  );

  const latest = results?.results?.[0];

  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white">
      <ToolHeader />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <GitCompare className="w-6 h-6 text-[#a855f7]" />
              <h1 className="text-3xl font-black font-['Cabinet_Grotesk'] tracking-tight" data-testid="cs-title">{t.cs_title || "Competitor Spy"}</h1>
              <span className="px-2 py-0.5 bg-[#a855f7]/15 text-[#c084fc] text-xs font-bold">€29/mo</span>
            </div>
            <p className="text-[var(--color-text-muted)]">{t.cs_desc || "Watch up to 5 competitors — see strengths, weaknesses and steal their wins."}</p>
          </div>
          <button onClick={() => setShowSetup(true)} data-testid="cs-add-btn"
            className="flex items-center gap-2 px-6 py-3 bg-[#a855f7] text-white font-bold uppercase text-sm hover:bg-purple-600">
            <Plus className="w-4 h-4" /> {t.cs_add || "Add"}
          </button>
        </div>

        {showSetup && (
          <Modal onClose={() => setShowSetup(false)} testid="cs-setup-modal">
            <form onSubmit={handleSetup}>
              <h2 className="text-xl font-black font-['Cabinet_Grotesk'] mb-6">{t.cs_setup || "Add competitors to watch"}</h2>
              <Field label={t.cs_your_url || "Your site URL *"}>
                <input required value={form.brand_url} onChange={(e) => setForm((p) => ({ ...p, brand_url: e.target.value }))}
                  placeholder="https://yoursite.com" className={inputCls} data-testid="cs-brand-url" />
              </Field>
              <Field label={t.cs_comp_urls || "Competitor URLs (comma-separated, max 5) *"}>
                <input required value={form.competitor_urls} onChange={(e) => setForm((p) => ({ ...p, competitor_urls: e.target.value }))}
                  placeholder="https://comp1.com, https://comp2.com" className={inputCls} data-testid="cs-comp-urls" />
              </Field>
              <button type="submit" disabled={loading} data-testid="cs-setup-submit"
                className="w-full mt-2 py-3 bg-[#a855f7] text-white font-bold uppercase hover:bg-purple-600 disabled:opacity-50 flex items-center justify-center gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                {t.cs_start || "Run first comparison"}
              </button>
            </form>
          </Modal>
        )}

        {spies.length > 0 && (
          <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
            {spies.map((s) => (
              <button key={s.spy_id} data-testid={`cs-spy-${s.spy_id}`} onClick={() => { setSelected(s); fetchResults(s.spy_id); }}
                className={`px-5 py-3 border whitespace-nowrap font-bold font-['Cabinet_Grotesk'] ${selected?.spy_id === s.spy_id ? "bg-[var(--color-surface)] border-[#a855f7]" : "bg-[var(--color-bg)] border-[var(--color-border)] text-[var(--color-text-muted)]"}`}>
                {s.brand_url.replace(/^https?:\/\//, "")}
              </button>
            ))}
          </div>
        )}

        {spies.length === 0 && !showSetup && (
          <EmptyState icon={GitCompare} title={t.cs_empty || "No competitors tracked yet"} sub={t.cs_empty_sub || "Add up to 5 competitors to see how you stack up."}
            cta={t.cs_add_first || "Add first competitor"} onClick={() => setShowSetup(true)} testid="cs-first-btn" />
        )}

        {selected && latest && (
          <div className="space-y-6" data-testid="cs-results">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black font-['Cabinet_Grotesk']">{selected.brand_url.replace(/^https?:\/\//, "")}</h2>
              <div className="flex gap-3">
                <button onClick={() => runCheck(selected.spy_id)} disabled={checking} data-testid="cs-recheck"
                  className="flex items-center gap-2 px-4 py-2 bg-[var(--color-surface)] border border-[var(--color-border)] text-sm font-bold hover:border-zinc-500 disabled:opacity-50">
                  {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  {t.cs_recheck || "Re-check"}
                </button>
                <button onClick={() => handleDelete(selected.spy_id)} className="p-2 text-[var(--color-text-muted)] hover:text-red-500" data-testid="cs-delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6" data-testid="cs-your-score">
              <p className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase tracking-wider mb-1">{t.cs_your_score || "Your estimated score"}</p>
              <p className="text-5xl font-black font-['JetBrains_Mono'] text-[#a855f7]">{latest.your_site?.estimated_score ?? 0}<span className="text-2xl text-[var(--color-text-muted)]">/100</span></p>
            </div>

            <div className="grid gap-4" data-testid="cs-competitors">
              {(latest.competitors || []).map((c, i) => (
                <div key={i} className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6" data-testid={`cs-comp-${i}`}>
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-bold font-['Cabinet_Grotesk']">{c.url.replace(/^https?:\/\//, "")}</span>
                    <span className="text-2xl font-black font-['JetBrains_Mono'] text-[#c084fc]">{c.estimated_score}</span>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4 text-sm">
                    <BulletList title={t.cs_strengths || "Their strengths"} color="text-red-400" items={c.strengths} />
                    <BulletList title={t.cs_weaknesses || "Their weaknesses"} color="text-amber-400" items={c.weaknesses} />
                    <BulletList title={t.cs_opps || "Opportunities for you"} color="text-emerald-400" items={c.opportunities_for_you} />
                  </div>
                </div>
              ))}
            </div>

            {latest.alerts?.length > 0 && (
              <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-6" data-testid="cs-alerts">
                <h3 className="text-sm font-bold text-[#FFE600] uppercase tracking-wider mb-4 font-['JetBrains_Mono'] flex items-center gap-2"><AlertTriangle className="w-4 h-4" />{t.cs_alerts || "Alerts"}</h3>
                <div className="space-y-3">
                  {latest.alerts.map((a, i) => (
                    <div key={i} className={`p-3 border flex items-start gap-3 ${PRIORITY_STYLE[a.priority] || PRIORITY_STYLE.medium}`} data-testid={`cs-alert-${i}`}>
                      <span className="mt-0.5 flex-shrink-0">{ALERT_ICON[a.type] || <AlertTriangle className="w-4 h-4" />}</span>
                      <div className="flex-1 text-sm">
                        <span className="uppercase text-[10px] font-black mr-2">{a.type}</span>
                        <span>{a.message}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {latest.top_action && (
              <div className="p-6 bg-[#FFE600]/5 border border-[#FFE600]/20" data-testid="cs-top-action">
                <div className="flex items-start gap-3">
                  <Zap className="w-5 h-5 text-[#FFE600] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[#FFE600] font-bold text-sm uppercase tracking-wider mb-1">{t.cs_top_action || "Your next move"}</p>
                    <p className="text-sm">{latest.top_action}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {loading && !latest && selected && (
          <div className="text-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#a855f7] mx-auto mb-4" />
            <p className="text-[var(--color-text-muted)]">{t.cs_loading || "Analysing competitive landscape…"}</p>
          </div>
        )}

        {finalCta}
      </div>
    </div>
  );
}

/* -------------- Shared small pieces, kept local to this file -------------- */
const inputCls = "w-full px-4 py-3 bg-[var(--color-bg)] border border-[var(--color-border)] text-white focus:border-[#a855f7] focus:outline-none mb-3";

function Field({ label, children }) {
  return (
    <div className="mb-1">
      <label className="block text-sm text-[var(--color-text-muted)] mb-1">{label}</label>
      {children}
    </div>
  );
}

function Modal({ children, onClose, testid }) {
  return (
    <div className="fixed inset-0 bg-[var(--color-bg)]/80 z-50 flex items-center justify-center p-4" data-testid={testid}>
      <div className="bg-[var(--color-surface)] border border-[var(--color-border)] p-8 max-w-lg w-full relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-[var(--color-text-muted)]"><X className="w-5 h-5" /></button>
        {children}
      </div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, sub, cta, onClick, testid }) {
  return (
    <div className="text-center py-20">
      <Icon className="w-16 h-16 text-zinc-700 mx-auto mb-6" />
      <h2 className="text-2xl font-black font-['Cabinet_Grotesk'] mb-3">{title}</h2>
      <p className="text-[var(--color-text-muted)] mb-8 max-w-md mx-auto">{sub}</p>
      <button onClick={onClick} data-testid={testid} className="px-8 py-4 bg-[#a855f7] text-white font-bold uppercase text-lg hover:bg-purple-600">
        <Plus className="w-5 h-5 inline mr-2" /> {cta}
      </button>
    </div>
  );
}

function BulletList({ title, color, items = [] }) {
  if (!items?.length) return null;
  return (
    <div>
      <p className={`text-xs font-['JetBrains_Mono'] uppercase tracking-wider mb-2 ${color}`}>{title}</p>
      <ul className="space-y-1.5">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-[var(--color-text)] leading-snug">
            <ArrowRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[var(--color-text-muted)]" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ToolHeader() {
  return (
    <header className="bg-[var(--color-bg)]/60 backdrop-blur-xl border-b border-white/10 px-6 py-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <a href="/" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center"><TrendingUp className="w-5 h-5 text-white" /></div>
          <span className="text-xl font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
        </a>
        <div className="flex items-center gap-4 text-sm text-[var(--color-text-muted)] font-['JetBrains_Mono'] uppercase">
          <a href="/dashboard">Dashboard</a>
          <a href="/">Home</a>
        </div>
      </div>
    </header>
  );
}

export function SignInGate({ title, sub, icon: Icon = CheckCircle2, children }) {
  return (
    <div className="min-h-screen bg-[var(--color-bg)] text-white px-6 pt-24 pb-12" data-testid="sign-in-gate">
      <div className="text-center">
        <Icon className="w-12 h-12 text-[#a855f7] mx-auto mb-4" />
        <h1 className="text-2xl font-black text-white font-['Cabinet_Grotesk'] mb-2">{title}</h1>
        <p className="text-[var(--color-text-muted)] mb-6">{sub}</p>
        <a href="/login" className="inline-block px-8 py-3 bg-[#a855f7] text-white font-bold uppercase" data-testid="sign-in-gate-login">Prihlásiť sa</a>
      </div>
      {children && <div className="max-w-4xl mx-auto">{children}</div>}
    </div>
  );
}
